// Copyright ©️ 2026 eVolve MEP, LLC

import type { TaskType } from '@evolvemep/api-ts';
import { Button } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useReactFlow } from '@xyflow/react';

import { useAssemblyEditor } from 'modules/Shop/CatalogSetup/PartEditor/useAssemblyEditor';
import { useCreateNodesFromTasks } from 'modules/Shop/CatalogSetup/PartEditor/utils';
import { TaskPickerModal } from 'modules/Shop/Tasks/TaskTypes/TaskPickerModal';

type AddToAssemblyButtonProps = {
  selectedTasks: TaskType[];
  onAdded: () => void;
};

const AddToAssemblyButton = ({ selectedTasks, onAdded }: AddToAssemblyButtonProps) => {
  const { createNodesFromTasks } = useCreateNodesFromTasks();
  const { addNode, publishing } = useAssemblyEditor();
  const { fitView, getZoom } = useReactFlow();

  const addSelectedTasks = () => {
    createNodesFromTasks(selectedTasks).map((v) => addNode(v));
    setTimeout(() => {
      void fitView({
        duration: 300,
        maxZoom: getZoom(),
        padding: 0,
      });
    }, 250);
  };

  return (
    <Button
      onClick={() => {
        addSelectedTasks();
        notifications.show({
          title: 'Successfully added',
          message: `Added ${selectedTasks.length} task${selectedTasks.length === 1 ? '' : 's'} to assembly.`,
          color: 'green',
        });
        onAdded();
      }}
      disabled={selectedTasks.length === 0 || publishing}
    >
      Add {selectedTasks.length > 0 ? `${selectedTasks.length} ` : ''}task{selectedTasks.length === 1 ? '' : 's'} to
      assembly
    </Button>
  );
};

export const AddTaskToAssembly = ({ disabled }: { disabled?: boolean }) => {
  const [opened, { open, close }] = useDisclosure(false);
  const { publishing } = useAssemblyEditor();
  return (
    <>
      <Button onClick={open} variant="light" size="sm" disabled={publishing || disabled}>
        Add tasks
      </Button>
      <TaskPickerModal
        title="Add tasks to assembly"
        opened={opened}
        onClose={close}
        renderAction={(selectedTasks) => <AddToAssemblyButton selectedTasks={selectedTasks} onAdded={close} />}
      />
    </>
  );
};
